import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { useData } from '../../contexts/DataContext';
import { FileText, Stethoscope, User, CheckCircle, Clock, Filter, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';

export default function MISReports() {
    const { assessments, adminEvaluations, patientQuestionnaires } = useData();
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [assessmentFilter, setAssessmentFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    // Calculate Test Availability dynamically from existing templates
    const testAvailability = useMemo(() => {
        const tests = {};
        
        // Count from adminEvaluations
        adminEvaluations?.forEach(evalItem => {
            const title = evalItem.title || 'Untitled Assessment';
            tests[title] = (tests[title] || 0) + 1;
        });

        // Count from patientQuestionnaires
        patientQuestionnaires?.forEach(qItem => {
            const title = qItem.title || 'Untitled Questionnaire';
            tests[title] = (tests[title] || 0) + 1;
        });

        return Object.entries(tests).map(([name, count]) => ({ name, count }));
    }, [adminEvaluations, patientQuestionnaires]);

    const totalTestsAvailable = testAvailability.reduce((sum, item) => sum + item.count, 0);

    // Split Assessments into Doctor and Therapist based on submitterRole
    const { doctorAssessments, therapistAssessments } = useMemo(() => {
        const docs = [];
        const therapists = [];

        (assessments || []).forEach(a => {
            const role = a.submitterRole || '';
            const isTherapist = role.toLowerCase().includes('therapist');
            
            if (isTherapist) {
                therapists.push(a);
            } else {
                docs.push(a);
            }
        });

        return { doctorAssessments: docs, therapistAssessments: therapists };
    }, [assessments]);

    // Non-destructive Filter Function
    const applyFilters = (data) => {
        return data.filter(item => {
            const matchDept = departmentFilter ? 'General'.toLowerCase().includes(departmentFilter.toLowerCase()) : true; // Department defaulting to General
            const matchAssmt = assessmentFilter ? (item.type || item.title || '').toLowerCase().includes(assessmentFilter.toLowerCase()) : true;
            const matchStatus = statusFilter ? (item.status || 'Finalized').toLowerCase().includes(statusFilter.toLowerCase()) : true;
            const matchDate = dateFilter ? (item.date || '').includes(dateFilter) : true;
            return matchDept && matchAssmt && matchStatus && matchDate;
        });
    };

    const filteredDoctorAssessments = useMemo(() => applyFilters(doctorAssessments), [doctorAssessments, departmentFilter, assessmentFilter, statusFilter, dateFilter]);
    const filteredTherapistAssessments = useMemo(() => applyFilters(therapistAssessments), [therapistAssessments, departmentFilter, assessmentFilter, statusFilter, dateFilter]);

    // Group Doctor Assessments by Department (Always 'General' as requested when data is unavailable)
    const groupedDoctorAssessments = useMemo(() => {
        const groups = {};
        filteredDoctorAssessments.forEach(a => {
            const dept = 'General';
            if (!groups[dept]) groups[dept] = [];
            groups[dept].push(a);
        });
        return groups;
    }, [filteredDoctorAssessments]);

    // Summary Cards Calculations
    const totalDoctorAssessments = doctorAssessments.length;
    const totalTherapistAssessments = therapistAssessments.length;
    const allAssessments = [...doctorAssessments, ...therapistAssessments];
    
    // Check status field. Treat 'finalized', 'completed' as completed. All others (e.g. pending, draft) as pending.
    const totalCompleted = allAssessments.filter(a => ['finalized', 'completed'].includes((a.status || 'Finalized').toLowerCase())).length;
    const totalPending = allAssessments.filter(a => !['finalized', 'completed'].includes((a.status || 'Finalized').toLowerCase())).length;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">MIS Reports</h2>
                    <p className="text-sm text-slate-500">Read-only visualization of system reports and assessments.</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="shadow-sm border-l-4 border-l-brand-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Tests Available</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalTestsAvailable}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600"><FileText className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-blue-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Doctor Assessments</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalDoctorAssessments}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><User className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-teal-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Therapist Assessments</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalTherapistAssessments}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600"><Stethoscope className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-green-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Completed</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalCompleted}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-green-50 flex items-center justify-center text-green-600"><CheckCircle className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-orange-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Pending</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{totalPending}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600"><Clock className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="shadow-sm bg-white">
                <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 border-b border-slate-100">
                    <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                        <Filter className="h-4 w-4" /> Filters
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => {
                        setDepartmentFilter('');
                        setAssessmentFilter('');
                        setStatusFilter('');
                        setDateFilter('');
                    }}>
                        <X className="h-4 w-4 mr-2" /> Clear Filters
                    </Button>
                </div>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Department</label>
                        <Input placeholder="e.g. General" value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Assessment</label>
                        <Input placeholder="Assessment Name" value={assessmentFilter} onChange={e => setAssessmentFilter(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Status</label>
                        <Input placeholder="e.g. Finalized" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Date</label>
                        <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} />
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Test Availability Table */}
                <div className="xl:col-span-1">
                    <Card className="shadow-sm h-full">
                        <CardHeader className="border-b border-slate-100 pb-4">
                            <CardTitle className="text-lg">Test Availability</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-slate-600">
                                    <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 font-semibold">Test Name</th>
                                            <th className="px-4 py-3 font-semibold text-right">Availability</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {testAvailability.length === 0 ? (
                                            <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">No tests available.</td></tr>
                                        ) : testAvailability.map((test, i) => (
                                            <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-4 py-3 font-medium text-slate-900">{test.name}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <Badge variant="outline">{test.count}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Assessments Tables */}
                <div className="xl:col-span-2 space-y-6">
                    {/* Doctor Assessments Table */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b border-slate-100 pb-4">
                            <CardTitle className="text-lg">Doctor Assessments</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {Object.keys(groupedDoctorAssessments).length === 0 ? (
                                <div className="p-8 text-center text-slate-400 text-sm">No doctor assessments found.</div>
                            ) : (
                                Object.entries(groupedDoctorAssessments).map(([dept, items]) => (
                                    <div key={dept}>
                                        <div className="bg-slate-50 px-4 py-2 font-bold text-slate-700 text-xs uppercase tracking-wider border-b border-slate-100">{dept}</div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left text-slate-600">
                                                <thead className="text-xs text-slate-500 bg-white border-b border-slate-100 hidden sm:table-header-group">
                                                    <tr>
                                                        <th className="px-4 py-3">Patient</th>
                                                        <th className="px-4 py-3">Assessment</th>
                                                        <th className="px-4 py-3">Doctor</th>
                                                        <th className="px-4 py-3">Status</th>
                                                        <th className="px-4 py-3">Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 bg-white">
                                                    {items.map((item, idx) => (
                                                        <tr key={idx} className="hover:bg-slate-50/60 transition-colors flex flex-col sm:table-row py-2 sm:py-0 border-b sm:border-0 last:border-0">
                                                            <td className="px-4 py-1 sm:py-3 font-medium text-slate-900">
                                                                <span className="sm:hidden font-semibold text-slate-500 mr-2 text-xs">Patient:</span>
                                                                {item.patientName || 'Unknown'}
                                                            </td>
                                                            <td className="px-4 py-1 sm:py-3">
                                                                <span className="sm:hidden font-semibold text-slate-500 mr-2 text-xs">Assessment:</span>
                                                                {item.type || item.title || 'Unknown Assessment'}
                                                            </td>
                                                            <td className="px-4 py-1 sm:py-3">
                                                                <span className="sm:hidden font-semibold text-slate-500 mr-2 text-xs">Doctor:</span>
                                                                {item.submitterName || 'Unknown'}
                                                            </td>
                                                            <td className="px-4 py-1 sm:py-3">
                                                                <span className="sm:hidden font-semibold text-slate-500 mr-2 text-xs">Status:</span>
                                                                <Badge variant={item.status === 'Draft' ? 'warning' : 'success'}>
                                                                    {item.status || 'Finalized'}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-4 py-1 sm:py-3 whitespace-nowrap text-slate-500">
                                                                <span className="sm:hidden font-semibold text-slate-500 mr-2 text-xs">Date:</span>
                                                                {item.date ? new Date(item.date).toLocaleDateString() : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Card>

                    {/* Therapist Assessments Table */}
                    <Card className="shadow-sm">
                        <CardHeader className="border-b border-slate-100 pb-4">
                            <CardTitle className="text-lg">Therapist Assessments</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-slate-600">
                                    <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 font-semibold">Patient</th>
                                            <th className="px-4 py-3 font-semibold">Therapist</th>
                                            <th className="px-4 py-3 font-semibold">Assessment</th>
                                            <th className="px-4 py-3 font-semibold">Status</th>
                                            <th className="px-4 py-3 font-semibold">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {filteredTherapistAssessments.length === 0 ? (
                                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No therapist assessments found.</td></tr>
                                        ) : filteredTherapistAssessments.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-4 py-3 font-medium text-slate-900">{item.patientName || 'Unknown'}</td>
                                                <td className="px-4 py-3">{item.submitterName || 'Unknown'}</td>
                                                <td className="px-4 py-3">{item.type || item.title || 'Unknown Assessment'}</td>
                                                <td className="px-4 py-3">
                                                    <Badge variant={item.status === 'Draft' ? 'warning' : 'success'}>
                                                        {item.status || 'Finalized'}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-slate-500">{item.date ? new Date(item.date).toLocaleDateString() : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
